/* eslint-disable multiline-ternary */
/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { Box, CircularProgress, Dialog, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { CancelPresentationRounded, ChevronLeft, ChevronRight, ShortcutOutlined } from '@mui/icons-material';
import CanvasJSReact from '../../canvasjs/canvasjs.react';
import Datagrid from '../../Datagrid';
import { useDispatch, useSelector } from 'react-redux';
import { downloadDataReport, downloadDisplacementPDF, listHydraulicsDetails } from '../../../../store/actions/hydraulics';
import { DISPLACEMENT_TYPE_COLORS, HYDRAULICS_CHART_COLORS } from '../../../../shared/config/hydraulics_constants';
import { listDisplacementResults } from '../../../../store/actions/hydraulicsDisplacement';
import { HYDRAULICS_RUN_TYPES, REPORT_INPUT_LIST, ROLLOVER_DISPLACEMENT_TYPE_SECONDARY, TOOLTIPS } from '../../../../utils/constants';
import ReportInputsModal from '../ReportInputsModal';
import { columns, customColumns } from './table_config';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import HydraulicsDetailsModal from '../HydraulicsRunModalDialog';
import { getDepthValueFromField } from '../../../../utils/depthSummaryExtractor';
import ReportsDownloadSelector from '../ReportsDownloadSelector';
import VersionText from '../VersionText';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const DisplacementChart = ({
  onCloseClick,
  isOpen,
  rowData,
  wellDetails,
  hydraulicWellsDetails
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const { displacementResults } = useSelector((state) => state.displacement);
  const dispatch = useDispatch();
  const hydraulics = useSelector((state) => state.hydraulics);
  const [loader, setLoader] = useState(false);
  const [titleHeader] = useState('Displacement');
  const [plotForPrimary, setPlotForPrimary] = useState('Pressure');
  const [plotForSecondary, setPlotForSecondary] = useState('Hole Depth');
  const [secondaryDepthField, setSecondaryDepthField] = useState([]);
  const [showReportInput, setShowReportInputs] = useState(false);
  const [tableList, setTableList] = useState(REPORT_INPUT_LIST.map(item => item.key));
  const [reportPreview, setReportPreview] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartOptions, setChartOptions] = useState({});
  const [showHydraulicsParameterDialog, setShowHydraulicsParameterDialog] = useState({
    open: false,
    data: {},
    dialogParameter: ''
  });
  const sbpField = 'connection_sbp';

  useEffect(() => {
    if (loader || hydraulics?.loading) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader, hydraulics]);

  useEffect(() => {
    const body = {
      runId: rowData?._id,
      wellsInfoId: rowData?.wellsInfoId
    };
    if (rowData?.isDisplacementResults) {
      dispatch(listDisplacementResults(body));
    }
  }, [rowData]);

  useEffect(() => {
    if (!displacementResults?.loading) {
      const displacement = displacementResults?.data?.[0];
      if (!displacement?.results?.length) {
        setTableRows([]);
        return;
      }

      const modifiedRecords = displacement.results.map((record) => ({
        ...record,
        _id: Math.random().toString(36).substring(2, 10)
      }));

      setTableRows(modifiedRecords);
      setShowTable(true);
      if (plotForPrimary === 'Pressure') {
        setChartData(displacementResults?.data[0]?.results);
      } else {
        const selected = secondaryDepthField.find(
          (f) => f.label === plotForSecondary
        );
        if (selected?.field?.length) {
          const [esdKey, ecdKey] = selected.field;
          const esdData = [];
          const ecdData = [];
          displacement.results.forEach((r) => {
            const strokes = r?.strokes ?? 0;

            const esdValue = getDepthValueFromField(r, esdKey) ?? 0;
            const ecdValue = getDepthValueFromField(r, ecdKey) ?? 0;

            esdData.push({
              x: strokes,
              y: esdValue,
            });

            ecdData.push({
              x: strokes,
              y: ecdValue,
            });
          });
          setChartData([
            { name: `ESD @ ${plotForSecondary} (${displacementResults?.units?.bh_esd?.label})`, data: esdData },
            { name: `ECD @ ${plotForSecondary} (${displacementResults?.units?.bh_esd?.label})`, data: ecdData }
          ]);
        }
      }
    } else {
      setShowTable(false);
    }
  }, [displacementResults?.data, displacementResults?.loading, plotForSecondary, plotForPrimary]);

  const secondaryField = secondaryDepthField.find(
    (type) => type.label === plotForSecondary
  );

  useEffect(() => {
    const secondaryFields = displacementResults?.data?.[0]?.secondary_fields;
    setSecondaryDepthField(secondaryFields || ROLLOVER_DISPLACEMENT_TYPE_SECONDARY);
  }, [displacementResults?.data]);

  const generateViewportMinMax = () => {
    const allDepthPoints = chartData.flatMap(item => item.data ?? []);
    const minMaxData = [...allDepthPoints];
    const keysToCheck = ['y'];

    const allValues = [];

    // Collect all values from the specified keys
    keysToCheck.forEach(key => {
      minMaxData.forEach(obj => {
        if (key in obj) {
          allValues.push(obj[key]);
        }
      });
    });

    // Calculate the overall lowest and highest values
    const overallLowest = Math.min(...allValues);
    const overallHighest = Math.max(...allValues);

    const results = {
      lowest: overallLowest,
      highest: overallHighest,
    };
    return results;
  };

  useEffect(() => {
    if (!chartData?.length) return;

    const options =
      plotForPrimary === 'Pressure'
        ? {
            height: 900,
            animationEnabled: true,
            theme: 'light2',
            backgroundColor: '#FFF',
            legend: {
              fontSize: 18,
              verticalAlign: 'top',
              horizontalAlign: 'center',
            },
            axisY: {
              title: 'Strokes',
              includeZero: true,
              reversed: true,
              titleFontSize: 22,
              labelFontSize: 16,
              gridThickness: 1,
            },
            axisX: {
              title: `Pressure (${displacementResults?.units?.sbp?.label ?? ''})`,
              minimum: 0,
              titleFontSize: 22,
              labelFontSize: 16,
              gridThickness: 1,
            },
            axisX2: {
              title: `Flow Rate (${displacementResults?.units?.flow_rate?.label ?? ''})`,
              titleFontSize: 22,
              labelFontSize: 16,
              minimum: 0,
              gridThickness: 1,
              labelPlacement: 'outside',
              labelFormatter: (e) => Math.round(e.value),
            },
            data: [
              {
                type: 'line',
                name: `Dynamic SBP (${displacementResults?.units?.sbp?.label ?? ''})`,
                axisXType: 'primary',
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_SBP,
                toolTipContent: `Strokes: {y} <br/> Dynamic SBP: {x} ${displacementResults?.units?.sbp?.label ?? ''
                }`,
                dataPoints:
                chartData?.map?.((record) => ({
                  x: record?.sbp ?? 0,
                  y: record?.strokes ?? 0,
                })) ?? [],
              },
              {
                type: 'line',
                name: `Connection SBP (${displacementResults?.units?.connection_sbp?.label ?? ''})`,
                axisXType: 'primary',
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_CONNECTION_SBP,
                toolTipContent: `Strokes: {y} <br/> Connection SBP: {x} ${displacementResults?.units?.connection_sbp?.label ?? ''
                }`,
                dataPoints:
                chartData?.map?.((record) => ({
                  x: record.connection_sbp ?? 0,
                  y: record?.strokes ?? 0,
                })) ?? [],
              },
              {
                type: 'line',
                axisXType: 'secondary',
                name: `Flow Rate (${displacementResults?.units?.flow_rate?.label ?? ''})`,
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_FLOW_RATE,
                toolTipContent: `Strokes: {y} <br/>Flow Rate: {x} ${displacementResults?.units?.flow_rate?.label ?? ''
                }`,
                dataPoints:
                chartData?.map?.((record) => ({
                  x: record?.flow_rate ?? 0,
                  y: record?.strokes ?? 0,
                })) ?? [],
              },
            ],
          }
        : {
            height: 900,
            animationEnabled: true,
            theme: 'light2',
            backgroundColor: '#FFF',
            legend: {
              fontSize: 18,
              verticalAlign: 'top',
              horizontalAlign: 'center',
            },
            axisY: {
              title: 'Strokes',
              includeZero: true,
              reversed: true,
              titleFontSize: 22,
              labelFontSize: 16,
              gridThickness: 1,
            },
            axisX: {
              title: `EMW @ ${plotForSecondary} (${displacementResults?.units?.bh_ecd?.label ?? ''})`,
              viewportMinimum: generateViewportMinMax()?.lowest - 0.1,
              viewportMaximum: generateViewportMinMax()?.highest + 0.1,
              interval: getOptimalInterval(generateViewportMinMax()?.lowest, generateViewportMinMax()?.highest, 20),
              titleFontSize: 22,
              labelFontSize: 16,
              gridThickness: 1,
            },
            axisX2: {
              title: `Flow Rate (${displacementResults?.units?.flow_rate?.label ?? ''})`,
              titleFontSize: 22,
              labelFontSize: 16,
              minimum: 0,
              gridThickness: 1,
              labelPlacement: 'outside',
              labelFormatter: (e) => Math.round(e.value),
            },
            data: [
              ...(Array.isArray(chartData) && chartData.length > 0
                ? chartData.map((dataset, i) => ({
                  type: 'line',
                  name: dataset?.name ?? `Series ${i + 1}`,
                  lineThickness: 2,
                  showInLegend: true,
                  color:
                  i === 0
                    ? DISPLACEMENT_TYPE_COLORS.ESD
                    : DISPLACEMENT_TYPE_COLORS.ECD,
                  toolTipContent: `Strokes: {y} <br/> ${dataset?.name ?? ''}: {x} ${displacementResults?.units?.bh_ecd?.label ?? ''
                  }`,
                  dataPoints:
                  dataset?.data?.map?.((d) => ({
                    x: d?.y ?? 0,
                    y: d?.x ?? 0,
                  })) ?? [],
                }))
                : []),
              {
                type: 'line',
                axisXType: 'secondary',
                name: `Flow Rate (${displacementResults?.units?.flow_rate?.label ?? ''})`,
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_FLOW_RATE,
                toolTipContent: `Strokes: {y} <br/>Flow Rate: {x} ${displacementResults?.units?.flow_rate?.label ?? ''
                }`,
                dataPoints:
                displacementResults?.data[0]?.results?.map?.((record) => ({
                  x: record?.flow_rate ?? 0,
                  y: record?.strokes ?? 0,
                })) ?? [],
              },
            ],
          };
    setChartOptions(options);
  }, [chartData, plotForPrimary, plotForSecondary, displacementResults]);

  const handlePDFReportDownload = () => {
    const body = {
      inputs: tableList,
      jobId: rowData?.jobId,
      wellsInfoId: rowData?.wellsInfoId,
      runId: rowData?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: rowData?.name,
      type: plotForPrimary,
      ...(plotForPrimary !== 'Pressure' && { depth: plotForSecondary }),
    };

    setLoader(true);
    dispatch(downloadDisplacementPDF(body, reportPreview, () => {
      setLoader(false);
    }));
  };

  const handleDataReportDownload = () => {
    setLoader(true);
    const body = {
      jobId: rowData?.jobId,
      wellsInfoId: rowData?.wellsInfoId,
      runId: rowData?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: rowData?.name,
      type: HYDRAULICS_RUN_TYPES?.DISPLACEMENT?.typeKey,
    };
    dispatch(downloadDataReport(body, () => {
      setLoader(false);
    }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseClick();
      }}
      maxWidth={'lg'}
      fullWidth={true}
      style={{
        marginRight: '20px'
      }}
      PaperProps={{
        sx: {
          overflow: 'visible'
        },
      }}
      className={classes.dialogModal}
    >
      {displacementResults?.loading || hydraulics?.loading
        ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ height: '700px' }}
          >
            <CircularProgress />
          </Box>
          )
        : (
          <>
      <Box className={classes.dialogTitle}>
        <Typography className={classes.titleHeader}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              {`${wellDetails?.Rig} | ${wellDetails?.Well} | ${rowData?.name}`}
            </Grid>
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              {rowData?.isDisplacementResults ? (
                <ReportsDownloadSelector
                  loading={loader || hydraulics.downloadDisplacementPdf === 'loading' || displacementResults.loading || hydraulics?.loading}
                  onDataDownload={handleDataReportDownload}
                  onPreviewOpen={(previewItem) => {
                    setShowReportInputs(true);
                    setReportPreview(previewItem);
                  }}
                />
              ) : null}
              <Tooltip title={TOOLTIPS.viewRunInputs}>
                <span>
                  <IconButton
                    disabled={hydraulics.downloadDisplacementPdf === 'loading' || displacementResults.loading || hydraulics?.loading}
                    onClick={() => {
                      dispatch(listHydraulicsDetails(rowData));
                      setShowHydraulicsParameterDialog({
                        open: true,
                        data: rowData,
                        dialogParameter: 'displacement'
                      });
                    }}>
                    <ShortcutOutlined fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                onClick={() => {
                  onCloseClick();
                }}
              >
                <CancelPresentationRounded fontSize='large' />
              </IconButton>
            </Grid>
          </Grid>
        </Typography>
      </Box>
      <Divider />
      {rowData?.isDisplacementResults ? <>
        <Grid container item xs={showTable ? 7 : 12} style={{ marginTop: showTable ? '1.25%' : '2%' }} alignItems="center" justifyContent={'center'} >
          {!displacementResults.loading && <>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Grid item className={classes.titleHeader}>
                {titleHeader}
              </Grid>
            </Box></>}
        </Grid>
        <Box className={classes.boxStyle}>
          <Grid container>
            <Grid item xs={showTable ? 7 : 12} sx={{
              p: 2,
              pt: 10,
              '.canvasjs-chart-container canvas': {
                cursor: loader || hydraulics?.loading ? 'wait !important' : ''
              }
            }}>
              {displacementResults?.loading
                ? (
                  <Box className={classes.progressBarStyle}>
                    <CircularProgress />
                  </Box>
                  )
                : chartOptions
                  ? (
                    <CanvasJSChart options={chartOptions} />
                    )
                  : (
                    <Typography variant="body2" color="textSecondary">
                      No data available to display.
                    </Typography>
                    )}

            </Grid>
            {!showTable && <Box className={classes.menuTogglerBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronLeft className={classes.iconSize} />
              </IconButton>
            </Box>}
            {showTable && <Grid item xs={5} marginTop={'2.5%'}>
              <Box className={classes.menuTogglerTableBtn}>
                <IconButton
                  onClick={() => {
                    setShowTable(!showTable);
                  }}>
                  <ChevronRight className={classes.iconSize} />
                </IconButton>
              </Box>
              <Box display="flex" justifyContent="flex-start" marginLeft={'2%'}>
                <Grid item xs={3}>
                  <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    width="400px"
                    gap={2}
                  >
                    {/* Type Dropdown */}
                    <Grid item>
                      <FormControl
                        fullWidth
                        sx={{ bgcolor: 'transparent', width: '160px' }}
                      >
                        <InputLabel id="filter-input-label">Type</InputLabel>
                        <Select
                          labelId="filter-select-id"
                          id="filter-id"
                          value={plotForPrimary}
                          label="Filter"
                          disabled={displacementResults?.loading}
                          onChange={(e) => setPlotForPrimary(e.target.value)}
                          sx={{
                            fontWeight: 'bolder',
                            fontSize: '18px',
                            background: 'unset',
                          }}
                        >
                          {['Pressure', 'EMW'].map((item, idItem) => (
                            <MenuItem
                              key={`item_${item}_${idItem}`}
                              value={item}
                              sx={{ fontSize: '18px', fontWeight: 'bold' }}
                            >
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Show secondary depth filter only when EMW is selected */}
                    {plotForPrimary === 'EMW' && (
                      <>
                        <Grid item>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            at
                          </Typography>
                        </Grid>

                        <Grid item>
                          <FormControl
                            fullWidth
                            sx={{ bgcolor: 'transparent', width: '200px' }}
                          >
                            <InputLabel id="depth-input-label">Depth</InputLabel>
                            <Select
                              labelId="depth-select-id"
                              id="depth-id"
                              value={plotForSecondary}
                              label="Depth"
                              disabled={displacementResults?.loading}
                              onChange={(e) => setPlotForSecondary(e.target.value)}
                              sx={{
                                fontWeight: 'bolder',
                                fontSize: '18px',
                                background: 'unset',
                              }}
                            >
                              {secondaryDepthField.map((item, idItem) => (
                                <MenuItem
                                  key={`item_${item}_${idItem}`}
                                  value={item.label}
                                  sx={{
                                    color: item?.color,
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {item.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                  </Box>
                </Grid>
              </Box>
              {showTable && <Grid item xs={12}>
                <Datagrid
                  fileName={`${wellDetails?.Well?.trim()}-${rowData?.name?.trim()}-Displacement`}
                  loading={displacementResults.loading}
                  data={tableRow}
                  sx={{
                    '.MuiDataGrid-cell': {
                      fontSize: '1rem',
                      padding: '2px',
                    },
                    '.MuiDataGrid-columnHeader': {
                      fontSize: '1.2rem',
                      fontWeight: '900 !important',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      overflow: 'visible',
                      lineHeight: '1.43rem',
                      whiteSpace: 'normal',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      minHeight: '0px'
                    },
                  }}
                  columns={plotForPrimary === 'Pressure' ? columns(displacementResults?.units, 'ECD', sbpField) : customColumns(displacementResults?.units, secondaryField, sbpField)}
                  disableStickyHeader={true}
                  autoHeight={false}
                  {...(tableRow.length <= 100
                    ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
                    : {})}
                  height={'900px'}
                  getRowHeight={() => 'auto'}
                  pageSize={100}
                  toolbar={{
                    options: {
                      columns: false,
                      filters: false,
                      density: false,
                      export: {
                        enabled: true,
                      }
                    },
                  }}
                />
              </Grid>}
            </Grid>}
          </Grid>
        </Box>
      </> : <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            textAlign: 'center',
            p: 4,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
          >
            No Results Found
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.disabled' }}
          >
            Try adjusting parameters to generate results.
          </Typography>
        </Box>
      </>}
      </>)}
      {
        showReportInput
          ? <ReportInputsModal
            isOpen={showReportInput}
            tableList={tableList}
            setTableList={setTableList}
            onCloseClick={() => {
              setShowReportInputs(false);
            }}
            generateReport={handlePDFReportDownload}
          />
          : null
      }
      {showHydraulicsParameterDialog.open && showHydraulicsParameterDialog?.data?.name
        ? (
          <HydraulicsDetailsModal
            selectedJob={wellDetails}
            isOpen={showHydraulicsParameterDialog.open}
            onCloseClick={() => {
              setShowHydraulicsParameterDialog({
                open: false,
                data: {}
              });
              dispatch({
                type: REDUX_ACTIONS.HYDRAULICS_SET_ID,
                payload: {
                  data: '',
                },
              });
            }}
            data={showHydraulicsParameterDialog.data}
            dialogParameter={showHydraulicsParameterDialog.dialogParameter}
            wellDetails={hydraulicWellsDetails}
          />
          )
        : null}
        <VersionText version={displacementResults?.ph_version} loading={displacementResults.loading} />
    </Dialog>
  );
};
DisplacementChart.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  rowData: PropTypes.any,
  wellDetails: PropTypes.any,
  hydraulicWellsDetails: PropTypes.any
};
DisplacementChart.defaultProps = {
  selectedJob: {}
};

export default DisplacementChart;
